import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, concatMap, from, Observable, of, Subscription, toArray } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

import moment from 'moment-timezone';
import 'moment-timezone';

import { CsvMentionApiModel } from '../../../api/mentions/model/csv-mention-api.model';
import { CsvField,
    CsvImportDialogService,
    FallbackInputTypeEnum,
    ImportedFile
} from '../../../Common/Dialogs/components/CsvImportDialog/csv-import-dialog.service';
import { MediaTypeEnum } from '../../../Common/Basic/enums/media-type.enum';
import { availableLanguages } from '../../../Core/Localization/models/available-languages.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { TimeHelper } from '../../../Common/Forms/helpers/time.helper';
import { timeFormatsEnum } from '../../../Core/Users/enums/time-formats.enum';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { TimeModel } from '../../../Common/Forms/models/time.model';
import { Mention } from '../../../Common/Mentions/models/mention';
import { MentionModel, MentionSaveFailResult } from '../../../api/mentions/model/mention.model';
import { Author } from '../models/author';
import { Publication } from '../models/publication';
import { MediaTypeModel } from '../models/media-type.model';
import { ImageModel } from '../../../Common/Misc/models/image.model';
import { TwentyFourHourModel } from '../../../Common/Forms/models/twenty-four-hour.model';
import { MinuteModel } from '../../../Common/Forms/models/minute.model';
import { meridiansEnum } from '../../../Common/Forms/enums/meridians.enum';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { isoLanguageNames } from '../../../Common/Forms/data/iso-language-names';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { MentionImportTypesEnum } from '../../../Common/MonitorShared/enum/mention-import-types.enum';
import { ProgressModel } from '../../../Common/Dialogs/models/progress.model';

export interface MentionImportRequestModel {
    mentionModels: MentionModel[];
    pushToMercury?: boolean;
    preserveContents?: boolean;
    matchToAgility?: boolean;
    fileName: string;
    folderIds: number[];
    tagIds: number[];
}

export interface MentionImportResponseModel {
    mentionIds?: string[];
    total?: number;
    duplicateMentionCount?: number;
    duplicateToPushToMercury?: number; // todo: change to the correct name when backend supplies this
    failedToSaveMentions?: MentionSaveFailResult[];
    failedToPushToMercury?: MentionSaveFailResult[];
}

export enum MentionImportTableFields {
    headline,
    body,
    publishedDateTime,
    publishedDate,
    publishedTime,
    timezone,
    outlet,
    author,
    mediaType,
    duration,
    url,
    imageUrls,
    sentiment,
    language,
    audienceReach,
    ave,
    city,
    state,
    country,
    engagementView,
    engagementShare,
    engagementComment,
    engagementQuote,
    engagementLike,
}

export enum MentionImportToCollectionEnum {
    folders = 'folders',
    tags = 'tags'
}

export class MentionImportParameters {
    importToSystemCollection: boolean = false;
    syncWithJournodex: boolean = true;
    matchToAgility: boolean = true;
    reportDestinationEmails: string[] = [];
}

class EngagementErrors {
    incompatibleMediaTypes: Set<string> = new Set();
    incompatibleMentionCount: number = 0;
}

export class MentionImportFileParameters {
    folders: any[] = [];
    tags: any[] = [];
}

@Injectable()
export class MentionCsvImportDialogService extends CsvImportDialogService<CsvMentionApiModel, MentionImportFileParameters> implements OnDestroy {

    subscriptions: Subscription[] = [];
    outputModels: CsvMentionApiModel[] = [];
    userPreferenceTimezone: string = null;

    publishedDateTimeDateValidationFormat: string;
    publishedDateTimeTimeValidationFormat: string;
    publishedDateValidationFormat: string;
    publishedTimeValidationFormat: string;

    sentimentOptions = [
        new DropdownOptionModel('Sentiment.POSITIVE', 1),
        new DropdownOptionModel('Sentiment.NEUTRAL', 0),
        new DropdownOptionModel('Sentiment.NEGATIVE', -1),
        new DropdownOptionModel('Sentiment.NOT_AVAILABLE', null)
    ];

    availableLanguages = [];
    languageOptions = [];
    languageFallbackDropdownOptions: DropdownOptionModel[] = [];
    dateValidationFormatDropdownOptions: DropdownOptionModel[] = [];
    timeValidationFormatDropdownOptions: DropdownOptionModel[] = [];

    progress: ProgressModel;
    private progress$ = new BehaviorSubject<ProgressModel>(null);

    headlineFieldDefinition = {
        fieldId: MentionImportTableFields.headline,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.headline}`),
        required: true,
        guessingHelperWords: ['headline', 'title'],
        addToModel: (model: CsvMentionApiModel, value: any) => {
            model.headline = value;
        },
        fallbackInputType: FallbackInputTypeEnum.text,
        defaultFallbackValue: this.localizationService.transform('Monitoring.mentionImporter.fallbackValue.headline'),
        maxLength: Mention.maxWordCount
    };
    bodyFieldDefinition = {
        fieldId: MentionImportTableFields.body,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.body}`),
        required: true,
        guessingHelperWords: ['body', 'content', 'article', 'text', 'snippet'],
        addToModel: (model: CsvMentionApiModel, value: any) => {
            model.body = value;
        },
        fallbackInputType: FallbackInputTypeEnum.text,
        defaultFallbackValue: this.localizationService.transform('Monitoring.mentionImporter.fallbackValue.mentionBody'),
        maxLength: Mention.maxWordCount
    };
    publishedDateTimeFieldDefinition = {
        fieldId: MentionImportTableFields.publishedDateTime,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.publishedDateTime}`),
        required: false,
        guessingHelperWords: ['published date and time', 'publisheddateandtime', 'published date time', 'publisheddatetime', 'date time', 'datetime'],
        validator: (value: string): boolean => {
            return !value || moment(value, this.publishedDateTimeDateValidationFormat + ' ' + this.publishedDateTimeTimeValidationFormat).isValid();
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            this.addDateAndTimeToModel(model, value, 'publishedDateTime');
        },
        fallbackInputType: FallbackInputTypeEnum.dateTime,
        defaultFallbackValue: null,
        dateValidationFormatDropdownOptions: this.dateValidationFormatDropdownOptions.slice(),
        timeValidationFormatDropdownOptions: this.timeValidationFormatDropdownOptions.slice()
    };
    publishedDateFieldDefinition = {
        fieldId: MentionImportTableFields.publishedDate,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.publishedDate}`),
        required: true,
        guessingHelperWords: ['published date', 'published', 'publisheddate', 'date'],
        validator: (value: string): boolean => {
            if (!value) {
                return true;
            }
            return this.parseLocalizedDate(value, this.publishedDateValidationFormat) !== null;
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            this.addDateToModel(model, value, 'publishedDate');
        },
        fallbackInputType: FallbackInputTypeEnum.date,
        defaultFallbackValue: null,
        dateValidationFormatDropdownOptions: this.dateValidationFormatDropdownOptions.slice()
    };
    publishedTimeFieldDefinition = {
        fieldId: MentionImportTableFields.publishedTime,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.publishedTime}`),
        required: false,
        guessingHelperWords: ['published time', 'publishedtime'],
        validator: (value: string): boolean => {
            return !value || (this.isValidTime(value) && moment(value.match(this.timeRegex)[0], this.publishedTimeValidationFormat, true).isValid());
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            this.addTimeToModel(model, value, 'publishedTime');
        },
        fallbackInputType: FallbackInputTypeEnum.time,
        defaultFallbackValue: null,
        timeValidationFormatDropdownOptions: this.timeValidationFormatDropdownOptions.slice()
    };
    timezoneFieldDefinition = {
        fieldId: MentionImportTableFields.timezone,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.timezone}`),
        required: false,
        guessingHelperWords: ['Timezone', 'Time-zone', 'TZ', 'TZN'],
        addToModel: (model: CsvMentionApiModel, value: any, fallback: any) => {
            const isValidTimezone = !!moment.tz.zone(value);
            if (isValidTimezone) {
                model.timezone = value;
            } else {
                model.timezone = fallback;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.timezone
    };
    outletFieldDefinition = {
        fieldId: MentionImportTableFields.outlet,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.outlet}`),
        required: false,
        guessingHelperWords: ['outlet', 'publication', 'media'],
        addToModel: (model: CsvMentionApiModel, value: any) => {
            model.outlet = value;
        },
        fallbackInputType: FallbackInputTypeEnum.text
    };
    authorFieldDefinition = {
        fieldId: MentionImportTableFields.author,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.author}`),
        required: false,
        guessingHelperWords: ['author', 'writer', 'journalist'],
        addToModel: (model: CsvMentionApiModel, value: any) => {
            model.author = value;
        },
        fallbackInputType: FallbackInputTypeEnum.text
    };
    mediaTypeFieldDefinition = {
        fieldId: MentionImportTableFields.mediaType,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.mediaType}`),
        required: false,
        guessingHelperWords: ['media type', 'mediatype', 'format', 'clip type'],
        validator: (value: string): boolean => {
            const lowercaseValue = value.toLocaleLowerCase();

            if (this.mediaTypeMap.has(lowercaseValue)) {
                value = this.mediaTypeMap.get(lowercaseValue);
            }

            const uppercaseValue = value.toLocaleUpperCase();

            return !value || this.allMediaTypes.includes(uppercaseValue);
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                const lowercaseValue = value.toLocaleLowerCase();
                if (this.mediaTypeMap.has(lowercaseValue)) {
                    value = this.mediaTypeMap.get(lowercaseValue);
                }

                const uppercaseValue = value.toLocaleUpperCase();

                if (this.allMediaTypes.includes(uppercaseValue)) {
                    model.mediaType = new MediaTypeModel(uppercaseValue as MediaTypeEnum);
                }
            } else if (value instanceof DropdownOptionModel) {
                model.mediaType = new MediaTypeModel(value.value);
            }
        },
        fallbackInputType: FallbackInputTypeEnum.dropdown,
        fallbackDropdownOptions: Object.values(MediaTypeEnum)
            .map(x => new DropdownOptionModel(`MediaType.${x}`, x)),
        defaultFallbackValue: new DropdownOptionModel(
            `MediaType.${MediaTypeEnum.ONLINE_NEWS}`,
            MediaTypeEnum.ONLINE_NEWS
        )
    };
    durationFieldDefinition = {
        fieldId: MentionImportTableFields.duration,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.duration}`),
        required: false,
        guessingHelperWords: ['duration', 'clipduration', 'clip duration', 'length', 'cliplength', 'clip length'],
        validator: (value: string): boolean => {
            return !value || moment.duration(value).as('seconds') > 0;
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            let duration;
            if (typeof value === 'string') {
                // momentjs only supports hour, minute, second format, while we don't want hour, so need to handle it manually
                const timeSlices = value.split(':');
                if (timeSlices.length > 1) {
                    if (timeSlices.length === 2) {
                        // assume the user inputs mm:ss
                        duration = parseInt(timeSlices[0], 10) * 60 + parseInt(timeSlices[1], 10);
                        if (duration < 3600) {
                            model.duration = duration;
                        }
                    }
                } else {
                    duration = parseInt(value, 10);
                    // don't set it if the duration is longer than an hour
                    if (!isNaN(duration) && duration < 3600) {
                        model.duration = duration;
                    }
                }
            } else if (value instanceof TimeModel) {
                duration = this.timeHelper.conventTimeToDuration(value);
                if (duration > 0) {
                    model.duration = duration;
                }
            }
        },
        fallbackInputType: FallbackInputTypeEnum.duration,
        defaultFallbackValue: new TimeModel()
    };
    urlFieldDefinition = {
        fieldId: MentionImportTableFields.url,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.url}`),
        required: false,
        guessingHelperWords: ['url', 'publishedurl', 'published url', 'published on', 'link'],
        transform: (value: string): string => {
            if (!value) {
                return '';
            }
            // Strip zero-width characters before decoding
            const cleanedValue = value.replace(/\u200B/, '');
            try {
                return decodeURIComponent(cleanedValue);
            } catch(e) {
                return cleanedValue;
            }
        },
        validator: (value: string): boolean => {
            return !value || this.isValidUrl(value) || this.findValidCisionUrls(value).length > 0;
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            const [url, originalUrl] = this.findValidCisionUrls(value);
            if (url) {
                model.url = url;
                model.originalUrl = originalUrl;
            } else {
                model.url = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.text,
        fallbackValueValidator: (value: string): boolean => {
            return !value || this.isValidUrl(value);
        },
        fallbackValueErrorMessage: this.localizationService.transform('Monitoring.mentionImporter.fallbackValue.invalidUrl')
    };
    imageUrlFieldDefinition = {
        fieldId: MentionImportTableFields.imageUrls,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.imageUrls}`),
        required: false,
        guessingHelperWords: ['image', 'images', 'image urls'],
        transform: (value: string): string => {
            if (!value || value === 'null' || value === 'undefined') {
                return '';
            }
            try {
                return decodeURIComponent(value);
            } catch(e) {
                return value;
            }
        },
        validator: (value: string): boolean => {
            return !value || value.split(';').map(x => x.trim()).every(val => this.isValidUrl(val));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            // since we assume it's a csv file, comma cannot be used as separator
            model.imageUrls = value.split(';').map(x => x.trim());
        },
        fallbackInputType: FallbackInputTypeEnum.text,
        fallbackValueValidator: (value: string): boolean => {
            return !value || value.split(';').map(x => x.trim()).every(val => this.isValidUrl(val));
        },
        fallbackValueErrorMessage: this.localizationService.transform('Monitoring.mentionImporter.fallbackValue.invalidUrl')
    };
    sentimentFieldDefinition = {
        fieldId: MentionImportTableFields.sentiment,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.sentiment}`),
        required: false,
        guessingHelperWords: ['sentiment', 'tone'],
        validator: (value: string): boolean => {
            return !value || this.sentimentRegex.test(value) || (!isNaN(parseFloat(value)) && Math.abs(parseFloat(value)) <= 1);
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                if (isNaN(parseFloat(value))) {
                    let sentimentIndex = Object.values(SearchCriteriaQuerySentimentsEnum)
                        .map(x => this.localizationService.transform(`Sentiment.${x}`).toLocaleLowerCase())
                        .indexOf(value.toLocaleLowerCase().trim());

                    if (sentimentIndex > -1) {
                        model.sentiment = this.sentimentOptions[sentimentIndex].value;
                    } else {
                        const englishToFrenchMap: Record<string, string> = {
                            'positive': 'Positif',
                            'neutral': 'Neutre',
                            'negative': 'Négatif',
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            'not available': 'Non disponible'
                        };
                        const normalizedValue = value.toLocaleLowerCase().trim();
                        const frenchValue = englishToFrenchMap[normalizedValue];
                        sentimentIndex = Object.values(SearchCriteriaQuerySentimentsEnum)
                            .map(x => this.localizationService.transform(`Sentiment.${x}`).toLocaleLowerCase())
                            .indexOf(frenchValue.toLocaleLowerCase());

                        if (sentimentIndex !== -1 && this.sentimentOptions[sentimentIndex]) {
                            model.sentiment = this.sentimentOptions[sentimentIndex].value;
                        }
                    }
                } else {
                    model.sentiment = parseFloat(value);
                }
            } else if (value instanceof DropdownOptionModel) {
                model.sentiment = value.value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.dropdown,
        fallbackDropdownOptions: this.sentimentOptions
    };
    languageFieldDefinition = {
        fieldId: MentionImportTableFields.language,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.language}`),
        required: false,
        guessingHelperWords: ['language', 'lang', 'lang.'],
        transform: (value: string): string => {
            const val = value?.trim().toLocaleLowerCase();
            const languageLabelIsoMap = new Map<string, string>([
                ['english', 'en'],
                ['french', 'fr']
            ]);
            const languageReplacementMap = new Map<string, string>([
                ['greek', 'Greek, Modern'],
                ['slovenian', 'Slovene'],
                ['azerbaijani', 'South Azerbaijani']
            ]);

            if (languageLabelIsoMap.has(val)) {
                return languageLabelIsoMap.get(val);
            }
            if (languageReplacementMap.has(val)) {
                return languageReplacementMap.get(val);
            }
            return value;
        },
        validator: (value: string): boolean => {
            return !value || this.noLanguageIndicators.indexOf(value.toLocaleLowerCase()) > -1
                || this.availableLanguages.indexOf(value.toLocaleLowerCase()) > -1;
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                if (this.noLanguageIndicators.indexOf(value.toLocaleLowerCase()) > -1) {
                    return;
                }
                if (value.length === 2) {
                    model.language = value;
                } else {
                    const languageIndex = this.languageOptions.map(lang => this.localizationService.transform(lang.label).toLocaleLowerCase())
                        .indexOf(value.toLocaleLowerCase());
                    model.language = this.languageOptions[languageIndex].value;
                }
            } else if (value instanceof DropdownOptionModel) {
                model.language = value.value;
            }

        },
        fallbackInputType: FallbackInputTypeEnum.dropdown,
        fallbackDropdownOptions: this.languageFallbackDropdownOptions
    };
    audienceReachFieldDefinition = {
        fieldId: MentionImportTableFields.audienceReach,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.audienceReach}`),
        required: false,
        guessingHelperWords: ['audience reach', 'audiencereach', 'audience', 'reach', 'viewership',
            'potential audience', 'potentialaudience', 'circulation', 'distribution', 'impressions'],
        validator: (value: string): boolean => {
            return !value || !isNaN(parseInt(value.replace(/\s*/g, ''), 10));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                model.audienceReach = parseInt(value.replace(/\s*/g, ''), 10);
            } else {
                model.audienceReach = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.number,
        max: Mention.maxAudienceReach,
        step: 1,
        integerOnly: true
    };
    aveFieldDefinition = {
        fieldId: MentionImportTableFields.ave,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.ave}`),
        required: false,
        guessingHelperWords: ['ave', 'a.v.e.', 'ad value', 'advalue', 'media cost', 'mediacost', 'ad equivalency'],
        validator: (value: string): boolean => {
            return !value || !isNaN(parseFloat(value.replace(/[\$\s,]*/g, '')));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                model.ave = parseFloat(value.replace(/[\$\s,]*/g, ''));
            } else {
                model.ave = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.number,
        max: Mention.maxAVE,
        step: 0.01,
        integerOnly: false
    };
    cityFieldDefinition = {
        fieldId: MentionImportTableFields.city,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.city}`),
        required: false,
        guessingHelperWords: ['city'],
        addToModel: (model: CsvMentionApiModel, value: any) => {
            model.city = value;
        },
        fallbackInputType: FallbackInputTypeEnum.autocomplete,
        maxLength: Mention.maxWordCount,
        fallbackSuggestionsFetcher: this.regionsService.autoCompleteCityLabelOnly.bind(this.regionsService)
    };
    stateFieldDefinition = {
        fieldId: MentionImportTableFields.state,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.state}`),
        required: false,
        guessingHelperWords: ['state', 'province', 'province/state', 'state/province'],
        addToModel: (model: CsvMentionApiModel, value: any) => {
            model.state = value;
        },
        fallbackInputType: FallbackInputTypeEnum.autocomplete,
        maxLength: Mention.maxWordCount,
        fallbackSuggestionsFetcher: this.regionsService.autoCompleteStateLabelOnly.bind(this.regionsService)
    };
    countryFieldDefinition = {
        fieldId: MentionImportTableFields.country,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.country}`),
        required: false,
        guessingHelperWords: ['country', 'nation', 'nation/country', 'country/nation'],
        addToModel: (model: CsvMentionApiModel, value: any) => {
            model.country = value;
        },
        fallbackInputType: FallbackInputTypeEnum.autocomplete,
        maxLength: Mention.maxWordCount,
        fallbackSuggestionsFetcher: this.regionsService.autoCompleteCountryLabelOnly.bind(this.regionsService)
    };
    engagementViewFieldDefinition = {
        fieldId: MentionImportTableFields.engagementView,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.engagementView}`),
        required: false,
        guessingHelperWords: [
            'views', 'view', 'view engagement', 'views engagement', 'view engagements', 'views engagements',
            'viewengagement', 'engagement view', 'engagement views', 'engagements view', 'engagements views',
            'engagementview', 'engagementviews', 'engagement - view', 'engagements - view', 'engagement - views',
            'engagements - views', 'view - engagement', 'view - engagements', 'views - engagement', 'views - engagements',
            'engagement | view', 'engagement | views', 'engagements | view', 'engagements | views',
            'view | engagement', 'views | engagement',
        ],
        validator: (value: string): boolean => {
            return !value || !isNaN(parseInt(value.replace(/\s*/g, ''), 10));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                model.engagementView = parseInt(value.replace(/\s*/g, ''), 10);
            } else {
                model.engagementView = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.number,
        defaultFallbackValue: 0,
        step: 1,
        integerOnly: true,
        placeholder: 'Monitoring.mentionImporter.fallbackValue.textPlaceholder'
    };
    engagementShareFieldDefinition = {
        fieldId: MentionImportTableFields.engagementShare,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.engagementShare}`),
        required: false,
        guessingHelperWords: [
            'shares', 'share', 'share engagement', 'share engagements', 'shares engagement', 'shares engagements',
            'engagement share', 'engagement shares', 'engagements share', 'engagements shares', 'engagementshare',
            'engagementshares', 'shareengagement', 'engagement - share', 'engagements - share', 'engagement - shares',
            'engagements - shares', 'share - engagement', 'share - engagements', 'shares - engagement', 'shares - engagements',
            'engagement | share', 'engagement | shares', 'engagements | share', 'engagements | shares',
            'share | engagement', 'shares | engagement', 'retweets', 'retweet', 'amplification',
            'repost', 'reposts'
        ],
        validator: (value: string): boolean => {
            return !value || !isNaN(parseInt(value.replace(/\s*/g, ''), 10));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                model.engagementShare = parseInt(value.replace(/\s*/g, ''), 10);
            } else {
                model.engagementShare = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.number,
        defaultFallbackValue: 0,
        step: 1,
        integerOnly: true,
        placeholder: 'Monitoring.mentionImporter.fallbackValue.textPlaceholder'
    };
    engagementCommentFieldDefinition = {
        fieldId: MentionImportTableFields.engagementComment,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.engagementComment}`),
        required: false,
        guessingHelperWords: [
            'comments', 'comment', 'comment engagement', 'comments engagement', 'comment engagements', 'comments engagements',
            'commentengagement', 'engagementcomment', 'engagementcomments', 'engagement comment', 'engagement comments',
            'engagements comment', 'engagements comments', 'engagement - comment', 'engagements - comment', 'engagement - comments',
            'engagements - comments', 'comment - engagement', 'comments - engagement', 'comment - engagements', 'comments - engagements',
            'comment | engagement', 'comments | engagement', 'comment | engagements', 'comments | engagements', 'engagement | comment',
            'engagements | comment', 'engagement | comments', 'engagements | comments', 'conversation', 'conversations', 'reply', 'replys'
        ],
        validator: (value: string): boolean => {
            return !value || !isNaN(parseInt(value.replace(/\s*/g, ''), 10));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                model.engagementComment = parseInt(value.replace(/\s*/g, ''), 10);
            } else {
                model.engagementComment = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.number,
        defaultFallbackValue: 0,
        step: 1,
        integerOnly: true,
        placeholder: 'Monitoring.mentionImporter.fallbackValue.textPlaceholder'
    };
    engagementQuotesFieldDefinition = {
        fieldId: MentionImportTableFields.engagementQuote,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.engagementQuote}`),
        required: false,
        guessingHelperWords: [
            'quotes', 'quote', 'quote engagement', 'quotes engagement', 'quote engagements', 'quotes engagements', 'quoteengagement',
            'engagementquote', 'engagementquotes', 'engagement quote', 'engagement quotes', 'engagements quote', 'engagements quotes',
            'engagement - quote', 'engagements - quote', 'engagement - quotes', 'engagements - quotes', 'quote - engagement', 'quotes - engagement',
            'quote - engagements', 'quotes - engagements', 'quote | engagement', 'quotes | engagement', 'quote | engagements', 'quotes | engagements',
            'engagement | quote', 'engagements | quote', 'engagement | quotes', 'engagements | quotes'
        ],
        validator: (value: string): boolean => {
            return !value || !isNaN(parseInt(value.replace(/\s*/g, ''), 10));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                model.engagementQuote = parseInt(value.replace(/\s*/g, ''), 10);
            } else {
                model.engagementQuote = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.number,
        defaultFallbackValue: 0,
        step: 1,
        integerOnly: true,
        placeholder: 'Monitoring.mentionImporter.fallbackValue.textPlaceholder'
    };
    engagementLikeFieldDefinition = {
        fieldId: MentionImportTableFields.engagementLike,
        label: this.localizationService.transform(`Monitoring.mentionImporter.fieldLabel.${MentionImportTableFields.engagementLike}`),
        required: false,
        guessingHelperWords: [
            'likes', 'like', 'like engagement', 'likes engagement', 'like engagements', 'likes engagements', 'likeengagement', 'engagementlike',
            'engagementlikes', 'engagement like', 'engagements like', 'engagement likes', 'engagements likes', 'engagement - like', 'engagements - like',
            'engagement - likes', 'engagements - likes', 'like - engagement', 'likes - engagement', 'like - engagements', 'likes - engagements',
            'like | engagement', 'likes | engagement', 'like | engagements', 'likes | engagements', 'engagement | like', 'engagements | like',
            'engagement | likes', 'engagements | likes', 'applause'
        ],
        validator: (value: string): boolean => {
            return !value || !isNaN(parseInt(value.replace(/\s*/g, ''), 10));
        },
        addToModel: (model: CsvMentionApiModel, value: any) => {
            if (typeof value === 'string') {
                model.engagementLike = parseInt(value.replace(/\s*/g, ''), 10);
            } else {
                model.engagementLike = value;
            }
        },
        fallbackInputType: FallbackInputTypeEnum.number,
        defaultFallbackValue: 0,
        step: 1,
        integerOnly: true,
        placeholder: 'Monitoring.mentionImporter.fallbackValue.textPlaceholder'
    };


    readonly validDateFormats: string[] = [
        'YYYY-MM-DD',
        'MMMM DD, YYYY',
        'DD-MM-YYYY',
        'DD-MM-YY',
        'D-M-YY',
        'YYYY-MM-DD',
        'YY-MM-DD',
        'M-D-YY',
        'DD-MMM-YY',
        'YY-MMM-DD',
        'MM/DD/YYYY',
        'M/D/YYYY',
        'DD/MM/YYYY',
        'D/M/YYYY',
        'M/D/YY'
    ];
    readonly sampleDate: moment.Moment = moment('2020-01-21');
    readonly validTimeFormats: string[] = [
        'hh:mm:ss A',
        'h:mm:ss A',
        'HH:mm:ss',
        'H:mm:ss',
    ];
    readonly sampleTime: moment.Moment = moment('2020-01-21 01:21:00');



    readonly timeRegex: RegExp = /[0-9]{1,2}:[0-9]{1,2}(:[0-9]{1,2})?.*(am|pm)?/i;
    readonly imageRegex: RegExp = /.*(image|img|jpg|png|gif|svg).*/i;
    readonly sentimentRegex: RegExp = /(positive|neutral|negative|not[_\s]?available)/i;
    readonly urlRegex: RegExp = /^https?:\/\/[^\/]+\.[^\/]+(\/.*)?/;
    readonly allMediaTypes = (Object.values(MediaTypeEnum) as string[]);
    /** Lowercase media type map used to map aliases or media types with spaces */
    readonly mediaTypeMap: Map<string, string> = new Map([
        [ 'online',        'online_news'   ],
        [ 'news',          'online_news'   ],
        [ 'online news',   'online_news'   ],
        [ 'blog',          'blogs'         ],
        [ 'press release', 'press_release' ],
        [ 'sina weibo',    'sina_weibo'    ],
        [ 'sina',          'sina_weibo'    ],
        [ 'weibo',         'sina_weibo'    ],
        [ 'x',             'twitter'       ],
        [ 'tik_tok',       'tiktok'        ],
        [ 'tik tok',       'tiktok'        ],
        [ 'blue sky',      'bluesky'       ],

    ]);
    readonly noLanguageIndicators = ['No linguistic content'].map(x => x.toLocaleLowerCase());

    readonly importBatchSize: number = 100;

    constructor(
        public localizationService: LocalizationService,
        public mentionsApi: MentionsApi,
        public currentUserStateService: CurrentUserStateService,
        public timeHelper: TimeHelper,
        public regionsService: JournodexRegionsService
    ) {
        super();
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    // default value for fallback timezone is user preference timezone
                    this.userPreferenceTimezone = preferences.timeZone;
                    this.setDefaultFallbackValues();
                }
            )
        );
        this.initializeDropdownOptions();
    }

    initializeDropdownOptions(): void {
        // this includes all two-digit codes and full names
        this.availableLanguages = availableLanguages.map(lang => this.localizationService.transform('Language.' + lang.id).toLocaleLowerCase())
            .concat(availableLanguages.map(lang => lang.id.toLocaleLowerCase()));
        // this is used for mapping user's language string
        this.languageOptions = availableLanguages.map(language => new DropdownOptionModel(language.name, language.id));
        // this is used for fallback dropdown
        this.languageFallbackDropdownOptions = Object.keys(isoLanguageNames)
            .map((key) => new DropdownOptionModel(this.localizationService.transform(`Language.${key}`), key))
            .sort((a, b) => a.label.localeCompare(b.label));

        this.dateValidationFormatDropdownOptions = this.validDateFormats
            .map(format => new DropdownOptionModel(this.sampleDate.format(format), format, format.replace('MMMM', 'Month').replace('MMM', 'Mon')));
        this.timeValidationFormatDropdownOptions = this.validTimeFormats
            .map(format => new DropdownOptionModel(this.sampleTime.format(format), format, format));
    }

    updateUrlRequired(required: boolean, csvFiles: ImportedFile<CsvMentionApiModel, MentionImportFileParameters>[]) {
        const urlField = this.fieldDefinitions?.find(x => x.fieldId === MentionImportTableFields.url);

        if (urlField) {
            urlField.required = required;
        } else {
            this.urlFieldDefinition.required = required;
        }

        // when not required, remove any existing URL required error messages
        if (!required) {
            csvFiles.forEach(file => {
                const URL = file.csvFields.find(column => {
                    return column.originalField.fieldId === MentionImportTableFields.url;
                });
                if (URL) {
                    URL.requiredError = false;
                }
            });
        }
    }

    setFieldDefinitions(importType: MentionImportTypesEnum): void {
        if (importType === MentionImportTypesEnum.GENERIC_CSV) {
            this.fieldDefinitions = [
                this.headlineFieldDefinition,
                this.bodyFieldDefinition,
                this.publishedDateTimeFieldDefinition,
                this.publishedDateFieldDefinition,
                this.publishedTimeFieldDefinition,
                this.timezoneFieldDefinition,
                this.outletFieldDefinition,
                this.authorFieldDefinition,
                this.mediaTypeFieldDefinition,
                this.durationFieldDefinition,
                this.urlFieldDefinition,
                this.imageUrlFieldDefinition,
                this.sentimentFieldDefinition,
                this.languageFieldDefinition,
                this.audienceReachFieldDefinition,
                this.aveFieldDefinition,
                this.cityFieldDefinition,
                this.stateFieldDefinition,
                this.countryFieldDefinition,
                this.engagementViewFieldDefinition,
                this.engagementShareFieldDefinition,
                this.engagementCommentFieldDefinition,
                this.engagementQuotesFieldDefinition,
                this.engagementLikeFieldDefinition,
            ];
        } else if (importType === MentionImportTypesEnum.CISION) {
            this.fieldDefinitions = [
                this.headlineFieldDefinition,
                this.bodyFieldDefinition,
                this.publishedDateFieldDefinition,
                this.outletFieldDefinition,
                this.authorFieldDefinition,
                this.mediaTypeFieldDefinition,
                this.urlFieldDefinition,
                this.sentimentFieldDefinition,
                this.audienceReachFieldDefinition,
                this.aveFieldDefinition,
                this.countryFieldDefinition,
                this.stateFieldDefinition,
                this.cityFieldDefinition
            ];
        }
        this.publishedDateTimeFieldDefinition.dateValidationFormatDropdownOptions = this.dateValidationFormatDropdownOptions.slice();
        this.publishedDateTimeFieldDefinition.timeValidationFormatDropdownOptions = this.timeValidationFormatDropdownOptions.slice();
        this.publishedDateFieldDefinition.dateValidationFormatDropdownOptions = this.dateValidationFormatDropdownOptions.slice();
        this.publishedTimeFieldDefinition.timeValidationFormatDropdownOptions = this.timeValidationFormatDropdownOptions.slice();
        this.languageFieldDefinition.fallbackDropdownOptions = this.languageFallbackDropdownOptions.slice();
    }

    setDefaultFallbackValues(): void {
        this.publishedDateTimeFieldDefinition.defaultFallbackValue = {
            date: new Date(moment().tz(this.userPreferenceTimezone).format('YYYY-MM-DD HH:mm')),
            time: this.timeHelper.getMidnightToday(this.userPreferenceTimezone, timeFormatsEnum.twelveHour)
        };
        this.publishedDateFieldDefinition.defaultFallbackValue = new Date(moment().tz(this.userPreferenceTimezone).format('YYYY-MM-DD HH:mm'));
        this.publishedTimeFieldDefinition.defaultFallbackValue = this.timeHelper.getMidnightToday(
            this.userPreferenceTimezone, timeFormatsEnum.twelveHour
        );
    }

    addDateAndTimeToModel(model: CsvMentionApiModel, value: any, field: string): void {
        if (typeof value === 'string') {
            // csv data row has valid string value
            model[field] = moment(value, this.publishedDateTimeDateValidationFormat + ' ' + this.publishedDateTimeTimeValidationFormat)
                .format('YYYY-MM-DD HH:mm');
        } else {
            // data field value is empty, we use fallback value which is value of date time picker, and the type is not string
            // NOTE: we don't deal with timezone here, and treat it as pure date/time string
            const dateTime = moment(value.date);
            dateTime.hour(value.time.hour + (value.time.meridian === meridiansEnum.PM ? 12 : 0));
            dateTime.minute(value.time.minute);
            model[field] = dateTime.format('YYYY-MM-DD HH:mm');
        }
    }

    /**
     * Attempts to parse a date string using multiple locales ('en' and 'fr').
     * This method ensures compatibility with both English and French date formats.
     *
     * @param value - The raw date string to be parsed (e.g., "28-Mar-24").
     * @param format - The expected date format (e.g., "DD-Mon-YY").
     * @returns A valid moment object if parsing succeeds with any locale; otherwise, returns null.
     */
    private parseLocalizedDate(value: string, format: string): moment.Moment | null {
        const locales = ['en', 'fr'];
        for (const locale of locales) {
            const parsedValue = moment(value, format, locale, true);
            if (parsedValue.isValid()) {
                return parsedValue;
            }
        }
        return null;
    }

    addDateToModel(model: CsvMentionApiModel, value: any, field: string): void {
        if (typeof value === 'string') {
            const parsedDate = this.parseLocalizedDate(value, this.publishedDateValidationFormat);
            model[field] = parsedDate ? parsedDate.format('YYYY-MM-DD HH:mm') : null;
        } else {
            // Fallback datetime value
            model[field] = moment(value).format('YYYY-MM-DD HH:mm');
        }
    }

    addTimeToModel(model: CsvMentionApiModel, value: any, field: string): void {
        if (typeof value === 'string') {
            // csv data row has valid time string value
            const time = moment(value.match(this.timeRegex)[0], this.publishedTimeValidationFormat);
            model[field] = new TimeModel(
                timeFormatsEnum.twentyFourHour,
                time.hour() as TwentyFourHourModel,
                time.minute() as MinuteModel
            );
        } else {
            // fallback value scenario
            model[field] = value;
        }
    }

    findHeaderRow(file: ImportedFile<CsvMentionApiModel, MentionImportFileParameters>) {
        file.hasHeaderRow = !(file.rawData[0].findIndex(column => {
            // Assume that the csv file does not have an empty header field or does not have a header field that contains date
            if (!column || moment(column, this.validDateFormats, true).isValid()) {
                return true;
            }
        }) > -1);
    }

    fileParameterFactory(): MentionImportFileParameters {
        return new MentionImportFileParameters();
    }

    findFieldMappingsFromDataRow(file: ImportedFile<CsvMentionApiModel, MentionImportFileParameters>) {
        const row = file.rawData[0];

        for (let i = 0; i < row.length; i++) {
            file.csvColumns.push({
                columnIndex: i,
                columnLabel: row[i] || this.localizationService.transform('Monitoring.mentionImporter.csvColumn.undefinedColumnName')
            });
        }

        for (let i = 0; i < row.length; i++) {
            let testValue, iter = 0;
            do {
                testValue = row[i];
                iter++;
            } while ((testValue === undefined || testValue === null) && iter < file.rawData.length);
            if (!testValue) {
                continue;
            }

            if (this.isValidDate(testValue)) {
                if (this.isValidTime(testValue)) {
                    // Assuming that time only is less than 12 chars
                    if (testValue.length > 11) {
                        this.setTargetColumn(file, MentionImportTableFields.publishedDateTime, i);
                        continue;
                    } else {
                        this.setTargetColumn(file, MentionImportTableFields.publishedTime, i);
                        continue;
                    }
                } else if (
                    // exclude other formats that can be seen as valie date
                    !(
                        this.isValidUrl(testValue)
                        || !isNaN(Number(testValue))
                        || (testValue.length < 6 && !isNaN(parseFloat(testValue)) && Math.abs(parseFloat(testValue)) <= 1)
                    )
                ) {
                    this.setTargetColumn(file, MentionImportTableFields.publishedDate, i);
                    continue;
                }
            } else if (this.availableLanguages.indexOf(testValue.toLocaleLowerCase()) > -1) {
                this.setTargetColumn(file, MentionImportTableFields.language, i);
                continue;
            }

            // Find 10 values and guess fields based of specific feature of majority
            let testValues = [];
            iter = 1;
            do {
                testValues = file.rawData.slice(0, Math.min(file.rawData.length, 50 * iter)).map(x => x[i]).filter(x => !!x);
                iter++;
            } while (testValues.length < 10 && 50 * iter <= file.rawData.length); // check rawData
            if (testValues.length === 0) {
                continue;
            }

            if (this.isMajority(testValues, x => this.allMediaTypes.includes(x.toLocaleUpperCase()))) {
                this.setTargetColumn(file, MentionImportTableFields.mediaType, i);
            } else if (this.isMajority(testValues, x => x.includes(':') && testValue.length <= 5)) {
                // assuming clip length is under 1 hour
                this.setTargetColumn(file, MentionImportTableFields.duration, i);
            } else if (this.isMajority(testValues, x => this.isValidUrl(x))) {
                if (this.isMajority(testValues, x => this.imageRegex.test(x))) {
                    this.setTargetColumn(file, MentionImportTableFields.imageUrls, i);
                } else if (!file.csvFields.find(x => x.originalField.fieldId === MentionImportTableFields.url).targetColumn) {
                    this.setTargetColumn(file, MentionImportTableFields.url, i);
                }
            } else if (this.isMajority(testValues, x => this.sentimentRegex.test(x) || (!isNaN(parseFloat(x)) && Math.abs(parseFloat(x)) <= 1))) {
                this.setTargetColumn(file, MentionImportTableFields.sentiment, i);
            }
        }
    }

    private isMajority(array: any[], filterFunction) {
        return array.filter(filterFunction).length > Math.floor(array.length / 2);
    }

    private isValidDate(date: string, strict: boolean = false): boolean {
        return moment(date).isValid();
    }

    // this is only used in finding header row because a valid date is also a valid time, so we need regex test for valid time specifically
    private isValidTime(time: string): boolean {
        return this.timeRegex.test(time);
    }

    private findValidCisionUrls(urls: string): string[] {
        const urlArray = urls.replace(/(=HYPERLINK|\(|\)|")/g, '').split(',').map(x => x.trim());

        return urlArray.length === 2 && urlArray.every(url => this.isValidUrl(url)) ? urlArray : [];
    }

    private isValidUrl(url: string): boolean {
        return this.urlRegex.test(url);
    }


    // pre import

    getValidationErrorsInOneFile(file: ImportedFile<CsvMentionApiModel, MentionImportFileParameters>): string[] {
        return [''];
    }


    importData(parameters: MentionImportParameters & { fileName: string }): Observable<MentionImportResponseModel[]> {
        const mentionModels: MentionModel[] = [];
        this.outputModels.forEach(model => {
            // convert to backend model
            const mention = this.convertCsvModelToMention(model).convertToApiMention();
            mentionModels.push(mention);
        });
        const apiCalls: { batch: MentionModel[]; request: Observable<MentionImportResponseModel> }[] = [];
        this.progress = {
            currentIndex: 0,
            totalItems: Math.ceil(mentionModels.length/this.importBatchSize)
        };
        this.progress$.next(this.progress);
        for (let i = 0; i < mentionModels.length; i += this.importBatchSize) {
            const batch = mentionModels.slice(i, i + this.importBatchSize);
            const uniqueFolderIds = new Set<number>(batch.map(b => b?.folderIds ?? []).flat());
            const uniqueTagIds = new Set<number>(batch.map(b => b?.tagIds ?? []).flat());

            const request: MentionImportRequestModel = {
                pushToMercury: parameters.importToSystemCollection,
                preserveContents: !parameters.syncWithJournodex,
                matchToAgility: parameters.matchToAgility,
                mentionModels: batch,
                fileName: parameters.fileName,
                folderIds: Array.from(uniqueFolderIds),
                tagIds: Array.from(uniqueTagIds),
            };
            apiCalls.push({
                batch: batch,
                request: this.mentionsApi.bulkImport(request).pipe(
                    map((response: MentionImportResponseModel) => {
                        const result = response;
                        result.total = batch.length;
                        return result;
                    }),
                    catchError((error) => {
                        console.error('Error for a request:', error);
                        const failedMentions = batch.map(x => {
                            return {
                                mention: x,
                                reason: null
                            } as MentionSaveFailResult;
                        });
                        return of({
                            mentionIds: [],
                            failedToSaveMentions: failedMentions,
                            failedToPushToMercury: failedMentions,
                            total: batch.length
                        } as MentionImportResponseModel);
                    })
                )
            });
        }

        return from(apiCalls).pipe(
            concatMap((call) => call.request.pipe(
                tap(() => {
                    this.progress = {
                        currentIndex: ++this.progress.currentIndex,
                        totalItems: Math.ceil(mentionModels.length/this.importBatchSize)
                    };
                    this.progress$.next(this.progress);
                }),
            )),
            toArray()
        );
    }

    private convertCsvModelToMention(model: CsvMentionApiModel): Mention {
        const mention = new Mention();
        mention.headline = model.headline;
        if (model.body) {
            mention.body = model.body;
        }

        let timezone = model.timezone;
        if (!timezone) {
            timezone = this.userPreferenceTimezone;
        }

        let datePublished: moment.Moment;
        if (model.publishedDateTime) {
            // if publishedDateTime is selected, it will be used and publishedDate would be overwritten
            datePublished = moment.tz(model.publishedDateTime, timezone);
        } else {
            datePublished = moment.tz(model.publishedDate, timezone);
            if (model.publishedTime) {
                datePublished.hour(model.publishedTime.hour +
                    (model.publishedTime.meridian === meridiansEnum.PM ? 12 : 0)
                );
                datePublished.minute(model.publishedTime.minute);
            } else {
                // if there is only date not time, use midnight
                datePublished.hour(0);
                datePublished.minute(0);
            }
        }
        // B/E accepts all date in UTC
        mention.datePublished = datePublished.toDate();
        if (model.outlet) {
            mention.publication = {
                name: model.outlet
            } as Publication;
        }
        if (model.author) {
            mention.author = {
                name: model.author
            } as Author;
        }
        if (model.mediaType) {
            mention.mediaType = model.mediaType;
        } else {
            mention.mediaType = new MediaTypeModel(MediaTypeEnum.ONLINE_NEWS);
        }
        if (model.duration || model.duration === 0) {
            mention.duration = model.duration;
        }
        if (model.url) {
            mention.url = model.url;
        }
        if (model.imageUrls?.length > 0) {
            mention.images = model.imageUrls.map(x => new ImageModel({ url: x, metadata: null }));
        }
        if (model.sentiment || model.sentiment === 0 || model.sentiment === null) {
            mention.sentiment = model.sentiment;
        }
        if (model.language) {
            mention.language = model.language;
        }
        if (model.audienceReach || model.audienceReach === 0) {
            mention.audienceReach = model.audienceReach;
        }
        if (model.ave || model.ave === 0) {
            mention.adValueUsd = model.ave;
        }
        if (model.folderIds) {
            mention.folderIds = model.folderIds;
        }
        if (model.tagIds) {
            mention.tagIds = model.tagIds;
        }
        if (model.city) {
            mention.cityName = model.city;
        }
        if (model.state) {
            mention.stateName = model.state;
        }
        if (model.country) {
            mention.countryName = model.country;
        }
        if (model.originalUrl) {
            mention.originalUrl = model.originalUrl;
        }
        if (model.engagementView || model.engagementView === 0) {
            mention.view = model.engagementView;
        }
        if (model.engagementShare || model.engagementShare === 0) {
            mention.share = model.engagementShare;
        }
        if (model.engagementComment || model.engagementComment === 0) {
            mention.reply = model.engagementComment;
        }
        if (model.engagementQuote || model.engagementQuote === 0) {
            mention.quote = model.engagementQuote;
        }
        if (model.engagementLike || model.engagementLike === 0) {
            mention.like = model.engagementLike;
        }

        return mention;
    }

    /**
     * Verify engagement data columns are not populated for non-social media types
     * @returns false if an error is present
     */
    private engagementValidator(csvTuple: CsvMentionApiModel): boolean {
        if (csvTuple.mediaType && !csvTuple.mediaType.isSocial) {
            if (csvTuple?.engagementComment >= 0 ||
                csvTuple?.engagementLike    >= 0 ||
                csvTuple?.engagementQuote   >= 0 ||
                csvTuple?.engagementShare   >= 0 ||
                csvTuple?.engagementView    >= 0
            ) {
                return false;
            }
        }
        return true;
    }

    // read all raw data into mention-like objects
    /**
     * Parse all raw data into mention-like objects
     * @returns
     * initial totalMentionCount: number |
     * parsed Mention Count: number |
     * incompatible fields: string[] |
     * engagement errors object: EngagementErrors
     */
    setOutputModels(): [number, number, string[], EngagementErrors] {
        const outputModels: CsvMentionApiModel[] = [];
        const incompatibleFields: string[] = [];
        const engagementErrors = new EngagementErrors();
        let totalMentionCount = 0;
        for (const importedFile of this.importedFiles) {
            const start = importedFile.hasHeaderRow ? 1 : 0;
            // loop through csv tuples
            for (let i = start; i < importedFile.rawData.length; i++) {
                totalMentionCount++;
                const model: CsvMentionApiModel = this.createEmptyOutputModel(importedFile);
                outputModels.push(model);
                let isMentionCompatible: boolean = true;
                // loop through csv columns/fields
                for (let j = 0; j < importedFile.csvFields.length; j++) {
                    const csvField = importedFile.csvFields[j];
                    if (csvField.targetColumn) {
                        let value = importedFile.rawData[i][csvField.targetColumn.columnIndex];
                        if (csvField.originalField.transform) {
                            value = csvField.originalField.transform(value);
                        }
                        let isFieldCompatible: boolean = true;
                        // if a value fails to pass the validator, consider the mention as incompatible and remove it below
                        if (csvField.originalField.validator && !csvField.originalField.validator(value)) {
                            if (incompatibleFields.indexOf(csvField.originalField.label) === -1) {
                                incompatibleFields.push(csvField.originalField.label);
                            }
                            isMentionCompatible = false;
                            isFieldCompatible = false;
                        }
                        if (isFieldCompatible) {
                            // This is why addToModel can take either string value (user provided) or object type (fallback value type)
                            if (value) {
                                csvField.originalField.addToModel(model, value, csvField.fallbackValue);
                            } else if (csvField.fallbackValue) {
                                csvField.originalField.addToModel(model, csvField.fallbackValue);
                            }
                        }
                    }
                }

                if (importedFile.fileParameters.folders?.length > 0) {
                    model.folderIds = importedFile.fileParameters.folders.map(folder => folder.id);
                }
                if (importedFile.fileParameters.tags?.length > 0) {
                    model.tagIds = importedFile.fileParameters.tags.map(tag => tag.id);
                }

                if (!this.engagementValidator(model)) {
                    engagementErrors.incompatibleMentionCount += 1;
                    engagementErrors.incompatibleMediaTypes.add(this.localizationService.transform(model.mediaType.name));
                    isMentionCompatible = false;
                }

                if (!isMentionCompatible) {
                    outputModels.pop();
                }
            }
        }
        this.outputModels = outputModels;
        return [totalMentionCount, outputModels.length, incompatibleFields, engagementErrors];
    }

    createEmptyOutputModel(file: ImportedFile<CsvMentionApiModel, MentionImportFileParameters>): CsvMentionApiModel {
        return {} as CsvMentionApiModel;
    }

    public updateExampleValue(file: ImportedFile<CsvMentionApiModel, MentionImportFileParameters>, field: CsvField<CsvMentionApiModel>) {
        super.updateExampleValue(file, field);
        if (field.exampleValue) {
            switch (field.originalField.fieldId) {
                case MentionImportTableFields.publishedDateTime:
                case MentionImportTableFields.publishedDate:
                case MentionImportTableFields.publishedTime:
                    this.updateDateTimeValidationFormatDropdownOptions(field);
                    break;
            }
        }
    }

    updateDateTimeValidationFormatDropdownOptions(field: CsvField<CsvMentionApiModel>) {
        let parsedMoment = null;

        let matchedDateFormat = null;
        if (field.originalField.dateValidationFormatDropdownOptions) {
            field.originalField.dateValidationFormatDropdownOptions.forEach(option => {
                // exampleValue can be a string with both date and time, so cannot use strict mode here, and need to use indexOf below
                parsedMoment = moment(field.exampleValue, option.value);
                if (parsedMoment.isValid() && field.exampleValue.indexOf(parsedMoment.format(option.value)) > -1 && matchedDateFormat !== '') {
                    if (matchedDateFormat) {
                        // If the moment can be parsed in multiple date formats, we will not pre-populate the format dropdown
                        matchedDateFormat = '';
                    } else {
                        matchedDateFormat = option.value;
                    }
                }
            });
            if (matchedDateFormat) {
                parsedMoment = moment(field.exampleValue, matchedDateFormat);
                field.originalField.dateValidationFormatDropdownOptions.forEach(option => {
                    option.label = option.value ? parsedMoment.format(option.value) : '';
                });
                field.selectedDateValidationFormat = field.originalField.dateValidationFormatDropdownOptions
                    .find(x =>  x.label && field.exampleValue.indexOf(x.label) > -1);
            }
        }

        let matchedTimeFormat = null;
        if (field.originalField.timeValidationFormatDropdownOptions) {
            field.originalField.timeValidationFormatDropdownOptions.forEach(option => {
                parsedMoment = moment(field.exampleValue, option.value);
                if (parsedMoment.isValid() && field.exampleValue.indexOf(parsedMoment.format(option.value)) > -1 && matchedTimeFormat !== '') {
                    if (matchedTimeFormat) {
                        matchedTimeFormat = '';
                    } else {
                        matchedTimeFormat = option.value;
                    }
                }
            });
            if (matchedTimeFormat) {
                parsedMoment = moment(field.exampleValue, matchedTimeFormat);
                field.originalField.timeValidationFormatDropdownOptions.forEach(option => {
                    option.label = option.value ? parsedMoment.format(option.value) : '';
                });
                field.selectedTimeValidationFormat = field.originalField.timeValidationFormatDropdownOptions
                    .find(x => x.label && field.exampleValue.indexOf(x.label) > -1);
            }
        }

        if (matchedDateFormat || matchedTimeFormat) {
            this.setDateTimeValidationFormat(field);
        }
    }

    setDateTimeValidationFormat(field: CsvField<CsvMentionApiModel>) {
        switch (field.originalField.fieldId) {
            case MentionImportTableFields.publishedDateTime:
                this.publishedDateTimeDateValidationFormat = field.selectedDateValidationFormat.value;
                this.publishedDateTimeTimeValidationFormat = field.selectedTimeValidationFormat.value;
                field.dateValidationFormatRequiredError = false;
                field.timeValidationFormatRequiredError = false;
                break;
            case MentionImportTableFields.publishedDate:
                this.publishedDateValidationFormat = field.selectedDateValidationFormat.value;
                field.dateValidationFormatRequiredError = false;
                break;
            case MentionImportTableFields.publishedTime:
                this.publishedTimeValidationFormat = field.selectedTimeValidationFormat.value;
                field.timeValidationFormatRequiredError = false;
                break;
        }
    }

    getProgress(): Observable<ProgressModel> {
        return this.progress$;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
